<template>
    <div class="rlms-flex rlms-items-center rlms-gap-20 rlms-fs-lg rlms-fw-600">
        <RouterLink :to="`/admin/users`" class="rlms-flex rlms-items-center"><span class="material-symbols-outlined">
                arrow_back </span></RouterLink>
    </div>

    <div class="admin-user-info">
        <div class="rlms-row rlms-justify-between">
            <!-- ID -->
            <div class="rlms-col-3">
                <div class="rlms-flex rlms-gap-10">
                    <span class="material-symbols-outlined">account_box</span>
                    <div class="rlms-flex rlms-flex-col">
                        <div class="rlms-text-secondary-lighter">User ID</div>
                        <p class="rlms-fw-600">{{ user?.id }}</p>
                    </div>
                </div>
            </div>

            <!-- Name -->
            <div class="rlms-col-3">
                <div class="rlms-flex rlms-gap-10">
                    <span class="material-symbols-outlined">person</span>
                    <div class="rlms-flex rlms-flex-col">
                        <div class="rlms-text-secondary-lighter">Name</div>
                        <p class="rlms-fw-600">{{ user?.name }}</p>
                    </div>
                </div>
            </div>

            <!-- Email Address -->
            <div class="rlms-col-3">
                <div class="rlms-flex rlms-gap-10">
                    <span class="material-symbols-outlined">mail</span>
                    <div class="rlms-flex rlms-flex-col">
                        <div class="rlms-text-secondary-lighter">Email Address</div>
                        <p class="rlms-fw-600">{{ user?.email }}</p>
                    </div>
                </div>
            </div>

            <div class="rlms-col-3">
                <div class="rlms-flex rlms-gap-10">
                    <span class="material-symbols-outlined">phone</span>
                    <div class="rlms-flex rlms-flex-col">
                        <div class="rlms-text-secondary-lighter">Phone</div>
                        <p class="rlms-fw-600"><span v-if="user?.phone">+{{ user?.dial_code }}{{ user?.phone }}</span>
                        </p>
                    </div>
                </div>
            </div>

            <!-- Creation Date -->
            <div class="rlms-col-3">
                <div class="rlms-flex rlms-gap-10">
                    <span class="material-symbols-outlined">calendar_month</span>
                    <div class="rlms-flex rlms-flex-col">
                        <div class="rlms-text-secondary-lighter">Creation Date</div>
                        <p class="rlms-fw-600">{{ formatDate(user?.created_at) }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="rlms-col-3">
      <div class="rlms-flex rlms-gap-10">
          <span class="material-symbols-outlined">handyman</span>
          <div class="rlms-flex rlms-flex-col">
              <div class="rlms-text-secondary-lighter">Transcription Service</div>
              <select v-model="transcriptionService" @change="onChangeTranscribeService">
                <option value="aws_transcribe">AWS Transcribe</option>
              </select>
          </div>
      </div>
    </div>

    <div class="rlms-w-100">
        <div class="pages-topbar rlms-mb-5" style="display: block;">
            <div class="pages-topbar-body">
                <div class="scrollable-content">
                    <ul>
                        <template v-for="(item, i) in sidebarLinks" :key="i">
                            <li
                                :class="{ 'active': isActive(item.to) || item?.childRouteNames?.includes($route.name) }">
                                <RouterLink :to="item.to"
                                    :class="{ 'link-active': isActive(item.to) || item?.childRouteNames?.includes($route.name) }"
                                    v-if="!item?.hide">
                                    <div class="pages-topbar-link-icon" style="width: 24px;">
                                        <img :src="`${item.icon}`" width="24" height="24" alt="">
                                    </div>
                                    <p class="rlms-fw-500 rlms-us-none">{{ item.name }}
                                    </p>
                                </RouterLink>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
        </div>
        <RouterView />
    </div>
</template>
<script setup>
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { formatDate } from '@/utils/format-helpers';
import { handleError } from '@/utils/handleError';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import PurchaseIcon from '@/assets/images/sidebar_blade_30265a60.svg';
import GroupsIcon from '@/assets/images/sidebar_blade_8ce6fbe5.svg'
import { showConfirmDialog, showToast } from '@/utils/swal';

const user = ref(null);
const transcriptionService = ref('whisper.cpp');
const loader = useLoaderStore();

const route = useRoute();

const userid = route.params.userid;

const isActive = (path) => {
    return route.path === path
}

// Define links with icons
const sidebarLinks = [
    {
        name: 'Purchase History',
        icon: PurchaseIcon,
        to: `/admin/users/${userid}`,
    },
    {
        name: 'Authorized Org',
        icon: GroupsIcon,
        to: `/admin/users/${userid}/groups`,
    },
];

const getData = async () => {
    try {
        loader.show();

        const { data } = await api.get(`admin/users/${userid}`);

        user.value = data?.data ?? null;
        if(user.value?.transcription_service) {
          transcriptionService.value = data?.data?.transcription_service
        }
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

onMounted(async () => await getData());

const onChangeTranscribeService = async () => {
  const result = await showConfirmDialog({
    title: 'Are you sure?',
    html: '<p id="text-message">Are you sure?</p><span class="rlms-text-danger-500" id="delete-confirm">Do you want to update transcribe service for this user?</span>',
    showCancelButton: true,
    confirmButtonColor: '#725cff',
    cancelButtonColor: '#725cff',
    confirmButtonText: `Confirm`,
    cancelButtonText: 'Cancel',
    confirmButtonClass: 'rlms-btn rlms-btn-primary',
    cancelButtonClass: 'rlms-btn rlms-btn-default',
  });

  if (result.isConfirmed) {
    try {
      loader.show();
      await api.post(`/admin/users/${userid}/transcription-service`, {
        transcription_service: transcriptionService.value
      })

      showToast({
        icon: 'success',
        title: 'Success!',
        text: `Transcription Service setting updated successfully.`
      });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
  }
}

</script>
<style scoped>
.admin-user-info {
    width: 100%;
    border-radius: 16px;
    background: aliceblue;
    padding: clamp(20px, -1533rem + 3200vw, 30px) clamp(20px, -1533rem + 3200vw, 24px);
}
</style>
